import React from "react";
import { ReactP5Wrapper } from "react-p5-wrapper";

function grid(p){
  const GRID_COLOR = [200, 200, 200, 80]; // Light gray with low alpha
  p.push();
  p.stroke(...GRID_COLOR);
  p.strokeWeight(1);
  let step = 40; // Size of grid squares

  // Draw grid much larger than canvas so it doesn't "run out" during camera movement
  let gridSize = 2000;
  for (let x = -gridSize; x <= gridSize; x += step) {
      p.line(x, -gridSize, x, gridSize);
  }
  for (let y = -gridSize; y <= gridSize; y += step) {
      p.line(-gridSize, y, gridSize, y);
  }
  p.pop();
}

const Whatfits = ({ p5ref, wt, ht, scale, fs, bgcolor }) => {
  const sketch = (p) => {
    // --- Color definitions ---
    let BLACK = [51, 51, 51];
    let PINK = [235, 93, 123];
    let BLUE = [93, 105, 235];
    let BEIGE = [255, 254, 246];

    let firstText = "Seek the perfect fit, not the perfect shape.";
    let secondText = "";
    let feelWidths = [];
    let doWidths = [];
    let spaceKerning = 4.75;

    const letter_extra_space = {
      a: 1.05, b: 1, c: 1, d: 1, e: 1.5, f: 0.5, g: 1, h: 1, i: 1, j: 1,
      k: 1, l: 0.1, m: 1, n: 1, o: 1, p: 1, q: 1, r: -0.2, s: 2, t: 0,
      u: 1, v: 0, w: 1, x: 1, y: 1, z: 1,
      A: 1, B: 1, C: 1, D: 1, E: 1, F: 1, G: 1, H: 1, I: 1, J: 1,
      K: 1, L: 0.5, M: 1, N: 1, O: 1, P: 1, Q: 1, R: 1, S: 2, T: 1,
      U: 1, V: 1, W: 1, X: 1, Y: 1, Z: 1
    };

    // Animation
    let rows = 5, cols = 5;
    let margin = 200;
    let circleSize = 20;
    let currentIndex = 0;
    let framesPerStep = 25;
    let stopped = false;

    let fadeSpeed = 0.5;
    let scaleSpeed = 0.05;
    let tickSpeed = 1;

    let scaleFactor = 1.8;
    // let scaleFactor = (circleSize + 2) * 1.8
    let targetScale = 1;
    let dissolveAlpha = 255;
    let tickAlpha = 0;
    let selectionStrokeWeight = 3;

    let rayRotation = 0.002;  // slow spin
    let rayPulse = 0.04;     // breathing


    // -------- helper ----------

    function preloadFont(url) {
      return new Promise((resolve) => {
        const link = document.createElement("link");
        link.href = url;
        link.rel = "stylesheet";
        link.onload = resolve;
        document.head.appendChild(link);
      });
    }

    // -------- PRELOAD ----------
    p.preload = async () => {
      await preloadFont("https://fonts.googleapis.com/css2?family=Lato&display=swap");
    };

    // -------- SETUP ----------
    p.setup = () => {
      p.createCanvas(wt, ht);
      p.rectMode(p.CENTER);
      p.ellipseMode(p.CENTER);
      p.textAlign(p.LEFT, p.CENTER);
      p.textSize(fs);

      try {
        p.textFont("Lato");
      } catch {
        p.textFont("sans-serif");
      }

      // Precompute widths
      for (let i = 0; i < firstText.length; i++) {
        let w = p.textWidth(firstText[i]);
        feelWidths[i] = w + (firstText[i] === " " ? spaceKerning : (letter_extra_space[firstText[i]] || 0));
      }
      for (let i = 0; i < secondText.length; i++) {
        let w = p.textWidth(secondText[i]);
        doWidths[i] = w + (secondText[i] === " " ? spaceKerning : (letter_extra_space[secondText[i]] || 0));
      }

    };

    function drawSunRays_old(cx, cy, innerR, outerR, alpha) {
      p.push();
      p.translate(cx, cy);

      let rays = 36;
      p.strokeWeight(3);
      p.stroke(PINK[0], PINK[1], PINK[2], alpha);

      for (let i = 0; i < rays; i++) {
        let a = (p.TWO_PI / rays) * i;
        let wobble = p.sin(p.frameCount * 0.015 + i) * 6;

        let x1 = p.cos(a) * innerR;
        let y1 = p.sin(a) * innerR;
        let x2 = p.cos(a) * (outerR + wobble);
        let y2 = p.sin(a) * (outerR + wobble);

        p.line(x1, y1, x2, y2);
      }

      p.pop();
    }

    function drawSunRays(cx, cy, innerR, outerR, alpha) {
      p.push();
      p.translate(cx, cy);
      p.rotate(rayRotation);

      let rays = 40;
      p.strokeWeight(3);
      p.stroke(PINK[0], PINK[1], PINK[2], alpha);

      for (let i = 0; i < rays; i++) {
        let a = (p.TWO_PI / rays) * i;

        // breathing motion
        let pulse = p.sin(rayPulse + i * 0.3) * 12;

        let x1 = p.cos(a) * innerR;
        let y1 = p.sin(a) * innerR;
        let x2 = p.cos(a) * (outerR + pulse);
        let y2 = p.sin(a) * (outerR + pulse);

        p.line(x1, y1, x2, y2);
      }

      p.pop();
    }



    // -------- TEXT RENDER ----------
    function drawStaticText() {
      p.push();
      p.textFont("Lato");
      p.fill(...BLACK);

      let centerX = p.width / 2;
      let firstY = p.height - 120;
      let secondY = firstY + 24;

      let firstWidth = feelWidths.reduce((a, b) => a + b, 0);
      let secondWidth = doWidths.reduce((a, b) => a + b, 0);

      let firstX = centerX - firstWidth / 2;
      let secondX = centerX - secondWidth / 2;

      let x = firstX;
      for (let i = 0; i < firstText.length; i++) {
        p.text(firstText[i], x, firstY);
        x += feelWidths[i];
      }
      x = secondX;
      for (let i = 0; i < secondText.length; i++) {
        p.text(secondText[i], x, secondY);
        x += doWidths[i];
      }

      p.pop();
    }

    // -------- MAIN GRID ----------
    function drawGrid() {
      p.push();

      let availableW = p.width - 2 * margin;
      let availableH = p.height - 2 * margin;
      let xSpacing = availableW / (cols - 1);
      let ySpacing = availableH / (rows - 1);

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          let x = margin + i * xSpacing;
          let y = margin + j * ySpacing;


          if (i === 2 && j === 2) {
            if (stopped) {
              p.stroke(...PINK, dissolveAlpha);
              p.fill(PINK)
            }
            else {
              p.stroke(...BLUE, dissolveAlpha);
              p.fill(BLUE)
            }

            p.rect(x, y, circleSize * 1.3, circleSize * 1.3);

          }
          else if (!stopped || dissolveAlpha > 0) {
            p.stroke(...BLUE, dissolveAlpha);
            p.strokeWeight(1);
            // p.noFill();
            p.fill(BLUE[0], BLUE[1], BLUE[2], dissolveAlpha)

            p.ellipse(x, y, circleSize);
          }
        }
      }

      // Moving frame
      if (!stopped) {
        let i = currentIndex % cols;
        let j = Math.floor(currentIndex / cols);
        let x = margin + i * xSpacing;
        let y = margin + j * ySpacing;

        p.noFill();
        p.stroke(...PINK);
        p.strokeWeight(selectionStrokeWeight);
        p.rect(x, y, (circleSize + 2) * scaleFactor, (circleSize + 2) * scaleFactor);

        if (i === 2 && j === 2) stopped = true;

        if (p.frameCount % framesPerStep === 0) {
          currentIndex++;
          if (currentIndex >= rows * cols) currentIndex = 0;
        }
      }

      // Merge animation
      if (stopped) {
        let x = margin + 2 * xSpacing;
        let y = margin + 2 * ySpacing;

        rayRotation += 0.002;  // slow spin
        rayPulse += 0.04;     // breathing

        scaleFactor = p.lerp(scaleFactor, targetScale, scaleSpeed);
        dissolveAlpha = p.lerp(dissolveAlpha, 0, fadeSpeed);
        tickAlpha = p.lerp(tickAlpha, 255, tickSpeed);

        // 🌞 sun rays
        p.noFill();
        p.stroke(...PINK);
        p.strokeWeight(selectionStrokeWeight);
        p.rect(x, y, (circleSize + 2) * scaleFactor, (circleSize + 2) * scaleFactor);

        drawSunRays(x, y, circleSize * 100, 0, 35);

      }

      p.pop();
    }

    // -------- DRAW LOOP ----------
    p.draw = () => {
      p.background(...BEIGE);
      grid(p);
      drawGrid();
      drawStaticText();
    };
  };

  return <div style={{ backgroundColor: bgcolor }}>
    <div ref={p5ref} style={{ transform: `scale(${scale})` }}>
      <ReactP5Wrapper sketch={sketch} />
    </div>
  </div>

};

export default Whatfits

